import { useState } from "react";
import {
  FaArrowLeft,
  FaBuilding,
  FaClipboardList,
  FaSatelliteDish,
  FaScroll,
  FaSearch,
  FaUniversity,
  FaUserAlt,
} from "react-icons/fa";
import ActionPageLayout from "../layouts/ActionPageLayout";
import usePageTitle from "../hooks/usePageTitle";
import useScrollToTopOnMessage from "../hooks/useScrollToTopOnMessage";
import { searchPilInfo } from "../services/pilInfoApi";

function displayValue(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
}

function formatMetric(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : String(value);
}

function formatDivisionLabel(value) {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, (character) => character.toUpperCase());
}

function formatPilDivision(value) {
  return value === "FV" ? "Fevicol" : value === "ROFF" ? "ROFF" : value;
}

function mapPilInfoResponse(response, selectedDivision) {
  const data = response.data || {};
  const memberProfile = data.memberProfile || {};
  const bankingPerformance = data.bankingPerformance || {};
  const clubDetails = data.clubDetails || {};
  const businessDevelopment = data.businessDevelopment || {};
  const contactDetails = data.contactDetails || {};
  const divisionInfo = data.divisionInfo || {};
  const duplicateGroup =
    divisionInfo.duplicateGroup ?? divisionInfo.duplicate_group;
  const divisionRows = Object.entries(divisionInfo)
    .filter(([label, value]) =>
      label === "duplicateGroup" || label === "duplicate_group"
        ? true
        : value !== null && value !== undefined && value !== "",
    )
    .map(([label, value]) => [
      formatDivisionLabel(label) === "Duplicate Group"
        ? "Division"
        : formatDivisionLabel(label),
      label === "duplicateGroup" || label === "duplicate_group"
        ? formatPilDivision(value || "FV")
        : typeof value === "object"
          ? JSON.stringify(value)
          : displayValue(value),
    ]);

  if (
    duplicateGroup === null ||
    duplicateGroup === undefined ||
    !Object.keys(divisionInfo).some(
      (label) => label === "duplicateGroup" || label === "duplicate_group",
    )
  ) {
    divisionRows.push(["Division", "Fevicol"]);
  }

  return {
    phone: displayValue(
      contactDetails.phone || data.phone || response.lookup?.phone,
    ),
    divisionLabel: formatPilDivision(
      selectedDivision || duplicateGroup || "FV",
    ),
    personal: [
      ["Contractor Name", displayValue(memberProfile.contractorName)],
      ["Address", displayValue(contactDetails.address)],
      ["City", displayValue(contactDetails.city)],
      ["State", displayValue(contactDetails.state)],
      ["PIN Code", displayValue(contactDetails.pincode)],
    ],
    club: [
      ["Club ID", displayValue(clubDetails.clubId)],
      ["Club Name", displayValue(clubDetails.clubName)],
      ["Club Location", displayValue(clubDetails.clubLocation)],
      ["BD Name", displayValue(businessDevelopment.bdName)],
      ["BD Mobile", displayValue(businessDevelopment.bdMobile)],
    ],
    pil: [
      ["Membership No", displayValue(memberProfile.membershipNumber)],
      ["Member Since", displayValue(memberProfile.memberSince)],
      ["Classification", displayValue(memberProfile.classification)],
    ],
    banking: [
      ["Banking Points QTD", formatMetric(bankingPerformance.pointsQTD)],
      ["Banking Points MTD", formatMetric(bankingPerformance.pointsMTD)],
      ["Banking QTD (kg)", formatMetric(bankingPerformance.qtyKgsQTD)],
      ["Banking MTD (kg)", formatMetric(bankingPerformance.qtyKgsMTD)],
      ["Banking YTD (kg)", formatMetric(bankingPerformance.qtyKgsYTD)],
      ["Banking YTD Points", formatMetric(bankingPerformance.pointsYTD)],
      [
        "Banking Six Month Points",
        formatMetric(bankingPerformance.pointsSixMonths),
      ],
      [
        "Banking Last 12 Months Points",
        formatMetric(bankingPerformance.pointsLast12Months),
      ],
      [
        "Banking Last 12 Months",
        formatMetric(bankingPerformance.qtyKgsLast12Months),
      ],
      ["Growth Percentage", formatMetric(bankingPerformance.growthPercentage)],
      ["Loyalty Points", displayValue(bankingPerformance.loyaltyPoints)],
    ],
    division: divisionRows,
  };
}

function InfoLine({ label, value }) {
  return (
    <div className="text-base leading-[1.45]">
      <span className="font-bold">{label}:</span>
      {value ? <span> {value}</span> : null}
    </div>
  );
}

function ResultCard({ className = "", icon, rows, rowsClassName = "", title }) {
  return (
    <section
      className={`rounded-[0.5rem] border border-[#d1d5db] bg-white px-4 py-5 shadow-[0_2px_8px_rgba(15,23,42,0.12)] ${className}`}
    >
      <h3 className="mb-5 flex items-center gap-2 border-b border-[#d9d9d9] pb-2 text-base font-bold text-[#111827]">
        {icon}
        {title}
      </h3>
      <div className={`grid gap-x-8 gap-y-3 ${rowsClassName}`}>
        {rows.map(([label, value]) => (
          <InfoLine key={label} label={label} value={value} />
        ))}
      </div>
    </section>
  );
}

function PilInfoPage() {
  const [searchType, setSearchType] = useState("phone");
  const [division, setDivision] = useState("FV");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pilId, setPilId] = useState("");
  const [pilResult, setPilResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  usePageTitle("PIL Information | Pargro");
  useScrollToTopOnMessage(errorMessage);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const phone = phoneNumber.trim();
    const selectedPilId = pilId.trim();

    if (searchType === "phone" && !phone) {
      setErrorMessage("Please enter a phone number.");
      return;
    }

    if (searchType === "pil" && !selectedPilId) {
      setErrorMessage("Please enter a PIL ID.");
      return;
    }

    setErrorMessage("");
    setIsSearching(true);

    try {
      const response = await searchPilInfo({
        searchType,
        phone,
        pilId: selectedPilId,
        division,
      });
      setPilResult(mapPilInfoResponse(response, division));
    } catch (error) {
      setPilResult(null);
      setErrorMessage(error.message);
    } finally {
      setIsSearching(false);
    }
  };

  const handleBackToSearch = () => {
    setPilResult(null);
    setErrorMessage("");
  };

  return (
    <ActionPageLayout
      title="PIL Information"
      icon={
        <FaSatelliteDish
          className="h-6 w-6 flex-none text-[#6b7280]"
          aria-hidden="true"
        />
      }
    >
      <section className="pt-2">
        {pilResult ? (
          <>
            <h2 className="mb-7 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
              <FaClipboardList
                className="h-8 w-8 flex-none text-[#c98655]"
                aria-hidden="true"
              />
              PIL Info for {pilResult.phone}
              {` | ${pilResult.divisionLabel}`}
            </h2>

            <div className="grid grid-cols-2 gap-6 max-[900px]:grid-cols-1">
              <ResultCard
                icon={
                  <FaUserAlt
                    className="h-4 w-4 flex-none text-[#432071]"
                    aria-hidden="true"
                  />
                }
                title="Personal Info"
                rows={pilResult.personal}
              />
              <ResultCard
                icon={
                  <FaBuilding
                    className="h-4 w-4 flex-none text-[#4f8fc8]"
                    aria-hidden="true"
                  />
                }
                title="Club Info"
                rows={pilResult.club}
              />
              <ResultCard
                icon={
                  <FaScroll
                    className="h-4 w-4 flex-none text-[#b7a7d8]"
                    aria-hidden="true"
                  />
                }
                title="PIL Info"
                rows={pilResult.pil}
                className="col-span-2 max-[900px]:col-span-1"
                rowsClassName="grid-cols-3 max-[900px]:grid-cols-1"
              />
              <div className="col-span-2 grid grid-cols-3 gap-6 max-[900px]:col-span-1 max-[900px]:grid-cols-1">
                <ResultCard
                  icon={
                    <FaUniversity
                      className="h-4 w-4 flex-none text-[#8d819f]"
                      aria-hidden="true"
                    />
                  }
                  title="PIL Banking Info"
                  rows={pilResult.banking}
                  className="col-span-2 max-[900px]:col-span-1"
                  rowsClassName="grid-cols-2 max-[720px]:grid-cols-1"
                />
                <ResultCard
                  icon={
                    <FaUniversity
                      className="h-4 w-4 flex-none text-[#8d819f]"
                      aria-hidden="true"
                    />
                  }
                  title="Division Info"
                  rows={pilResult.division}
                  rowsClassName="grid-cols-1"
                />
              </div>
            </div>

            <div className="flex justify-center py-12">
              <button
                className="inline-flex items-center gap-2 rounded-md bg-[#6c757d] px-5 py-2 text-base font-medium text-white shadow transition hover:bg-[#5c656d] focus-visible:bg-[#5c656d]"
                type="button"
                onClick={handleBackToSearch}
              >
                <FaArrowLeft
                  className="h-4 w-4 flex-none text-[#7c5bd6]"
                  aria-hidden="true"
                />
                Back to Search
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="mb-3 flex items-center gap-3 text-[1.75rem] font-bold text-[#171717] max-[720px]:text-2xl">
              <FaSearch
                className="h-8 w-8 flex-none text-[#6d4b8a]"
                aria-hidden="true"
              />
              Check PIL Info
            </h2>

            {errorMessage && (
              <p
                className="mb-3 rounded-md border border-[#f5c2c7] bg-[#f8d7da] px-4 py-3 text-sm text-[#842029]"
                role="alert"
              >
                {errorMessage}
              </p>
            )}

            <form
              className="overflow-hidden rounded-lg border border-[#e2e8f0] bg-white shadow-[0_4px_16px_rgba(15,23,42,0.08)]"
              onSubmit={handleSubmit}
            >
              <div className="border-b border-[#e5e7eb] bg-[#f8fafc] px-5 py-4 max-[720px]:px-4">
                <label className="block w-full max-w-[420px]">
                  <span className="mb-2 block text-sm font-bold">
                    PIL Division *
                  </span>
                  <select
                    className="h-11 w-full rounded-md border border-[#cbd5e1] bg-white px-3 text-base text-[#111827] outline-none transition focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.18)]"
                    value={division}
                    onChange={(event) => setDivision(event.target.value)}
                    required
                  >
                    <option value="FV">Fevicol</option>
                    <option value="ROFF">ROFF</option>
                  </select>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-5 px-5 py-5 max-[900px]:grid-cols-1 max-[720px]:px-4">
                <label
                  className={`rounded-lg border p-5 transition ${
                    searchType === "phone"
                      ? "border-[#0d73ff] bg-[#f7faff] shadow-[0_0_0_1px_rgba(13,115,255,0.12)]"
                      : "border-[#d1d5db] bg-white hover:border-[#9ca3af]"
                  }`}
                >
                  <span className="mb-3.5 flex items-center gap-2.5 text-base font-bold">
                    <input
                      className="h-4 w-4 accent-[#0d73ff]"
                      type="radio"
                      name="searchType"
                      value="phone"
                      checked={searchType === "phone"}
                      onChange={() => {
                        setSearchType("phone");
                        setErrorMessage("");
                      }}
                    />
                    Search by Phone
                  </span>

                  <span className="mb-2 block text-base">Mobile Number</span>
                  <input
                    className="h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.25)] disabled:bg-[#e9ecef]"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter 10-digit mobile number"
                    value={phoneNumber}
                    disabled={searchType !== "phone"}
                    onChange={(event) => setPhoneNumber(event.target.value)}
                    autoFocus
                  />
                </label>

                <label
                  className={`rounded-lg border p-5 transition ${
                    searchType === "pil"
                      ? "border-[#0d73ff] bg-[#f7faff] shadow-[0_0_0_1px_rgba(13,115,255,0.12)]"
                      : "border-[#d1d5db] bg-white hover:border-[#9ca3af]"
                  }`}
                >
                  <span className="mb-3.5 flex items-center gap-2.5 text-base font-bold">
                    <input
                      className="h-4 w-4 accent-[#0d73ff]"
                      type="radio"
                      name="searchType"
                      value="pil"
                      checked={searchType === "pil"}
                      onChange={() => {
                        setSearchType("pil");
                        setErrorMessage("");
                      }}
                    />
                    Search by PIL ID
                  </span>

                  <span className="mb-2 block text-base">PIL ID</span>
                  <input
                    className="h-[38px] w-full rounded-md border border-[#d8dee6] bg-white px-3 text-base outline-none transition placeholder:text-[#4b5563] focus:border-[#0d73ff] focus:shadow-[0_0_0_4px_rgba(13,115,255,0.25)] disabled:bg-[#e9ecef]"
                    type="text"
                    placeholder="Enter PIL ID"
                    value={pilId}
                    disabled={searchType !== "pil"}
                    onChange={(event) => setPilId(event.target.value)}
                  />
                </label>
              </div>

              <div className="flex justify-end border-t border-[#e5e7eb] bg-[#f8fafc] px-5 py-4 max-[720px]:px-4">
                <button
                  className="inline-flex h-11 min-w-[132px] items-center justify-center rounded-md bg-[#1773f8] px-6 text-base font-semibold text-white shadow-sm transition hover:bg-[#0d63dc] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#93c5fd] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#8bbcf8] max-[720px]:w-full"
                  disabled={isSearching}
                  type="submit"
                >
                  {isSearching ? "Checking..." : "Check Info"}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </ActionPageLayout>
  );
}

export default PilInfoPage;
